import { JobPreset, JobPresetAdmin } from 'interfaces/jobPreset';
import { stringify } from 'qs';
import { ArrayParam, encodeQueryParams } from 'use-query-params';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';

export const getJobPresets = (steps: string[], signal?: AbortSignal, studyId?: string) => {
  const stringParams = stringify(encodeQueryParams({ steps: ArrayParam }, { steps }), {
    arrayFormat: 'repeat',
  });
  const studyParam = studyId ? `&studyId=${encodeURIComponent(studyId)}` : '';

  return apiRequestHandlerPromise<JobPreset[]>({
    url: `job-preset?${stringParams}${studyParam}`,
    method: 'GET',
    signal,
  });
};

export const getJobPresetVersions = (id: string, signal?: AbortSignal) =>
  apiRequestHandlerPromise<JobPreset[]>({
    url: `job-preset/${id}/versions`,
    method: 'GET',
    signal,
  });

export const getJobPresetsForAdmin = (signal?: AbortSignal) =>
  apiRequestHandlerPromise<JobPresetAdmin[]>({
    url: 'job-preset/admin',
    method: 'GET',
    signal,
  });

export interface JobPresetLifecycleUpdate {
  id: string;
  isPublished?: boolean;
  isGlobal?: boolean;
}

export const bulkUpdateJobPresetLifecycle = (updates: JobPresetLifecycleUpdate[]) =>
  apiRequestHandlerPromise<JobPresetAdmin[]>({
    url: 'job-preset/admin/lifecycle',
    method: 'PATCH',
    data: JSON.stringify({ updates }),
  });

export const createJobPreset = (data: {
  name: string;
  stains: string[];
  steps: string[];
  presetJson: any;
  sourceStudyId: string | null;
}) => {
  return apiRequestHandlerPromise<JobPreset>({
    url: `job-preset`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const createGlobalJobPreset = (data: {
  name: string;
  stains: string[];
  steps: string[];
  presetJson: any;
  sourceStudyId: string | null;
}) =>
  apiRequestHandlerPromise<JobPreset>({
    url: 'job-preset/admin',
    method: 'POST',
    data: JSON.stringify(data),
  });

export const updateJobPreset = (data: {
  id: string;
  name?: string;
  stains: string[];
  steps: string[];
  presetJson: any;
  sourceStudyId: string | null;
}) => {
  return apiRequestHandlerPromise<JobPreset>({
    url: `job-preset/${data?.id}`,
    method: 'PUT',
    data: JSON.stringify(data),
  });
};

export const createJobPresetVersion = (data: {
  id: string;
  name?: string;
  stains?: string[];
  steps?: string[];
  presetJson?: any;
  sourceStudyId?: string | null;
}) => {
  const { id, ...versionData } = data;
  return apiRequestHandlerPromise<JobPreset>({
    url: `job-preset/${id}/versions`,
    method: 'POST',
    data: JSON.stringify(versionData),
  });
};

export const createGlobalJobPresetVersion = (data: {
  id: string;
  name?: string;
  stains?: string[];
  steps?: string[];
  presetJson?: any;
  sourceStudyId?: string | null;
}) => {
  const { id, ...versionData } = data;
  return apiRequestHandlerPromise<JobPreset>({
    url: `job-preset/admin/${id}/versions`,
    method: 'POST',
    data: JSON.stringify(versionData),
  });
};

export const setJobPresetPublished = ({ id, isPublished }: { id: string; isPublished: boolean }) =>
  apiRequestHandlerPromise<JobPreset>({
    url: `job-preset/${id}/published`,
    method: 'PATCH',
    data: JSON.stringify({ isPublished }),
  });

export const setJobPresetGlobal = ({ id, isGlobal }: { id: string; isGlobal: boolean }) =>
  apiRequestHandlerPromise<JobPreset>({
    url: `job-preset/${id}/global`,
    method: 'PATCH',
    data: JSON.stringify({ isGlobal }),
  });

export const deleteJobPreset = (id: string) => {
  return apiRequestHandlerPromise({
    url: `job-preset/${id}`,
    method: 'DELETE',
  });
};
